import {PointPlacemark, VehiclePlacemark} from '@logistics-frontend/blocks/lpc/map'
import {Order as OrderType, Point} from '@yandex-taxi/js-integration-api'
import {useHistoryItemQuery, useOrdersState} from '@yandex-taxi/react-integration-api'
import {useMapContext, useMapUtil} from '@yandex-taxi/react-ymaps'
import React, {useEffect, useMemo, useRef} from 'react'

import {BOUNDS_ANIMATION} from 'src/components/consts'

import {useShouldBoundsSettings} from '../../components/map-bounds'
import {isTariff} from '../utils'

type PointsProps = {
  points: Point[]
  vehicle?: Point
  setBounds: (newBounds: Point[]) => void
}

const Points: React.FC<PointsProps> = ({points, vehicle, setBounds}) => {
  const utilBounds = useMapUtil().bounds
  const map = useMapContext()
  const shouldBounds = useShouldBoundsSettings()
  const loading = useRef(false)

  useEffect(() => {
    if (points && shouldBounds && !loading.current) {
      loading.current = true
      const allPoints = vehicle ? points.concat([vehicle]) : points
      const bounds = utilBounds.fromPoints(allPoints)
      setBounds(bounds)
      map.setBounds(bounds, BOUNDS_ANIMATION).then(() => (loading.current = false))
    }
  }, [utilBounds, points, map, vehicle, shouldBounds, setBounds])

  if (!points) {
    return null
  }

  return (
    <>
      {points.map((coords, index) => (
        <PointPlacemark key={index} coords={coords} index={index} />
      ))}
      {vehicle && <VehiclePlacemark coords={vehicle} />}
    </>
  )
}

type OrderPointsProps = {
  orderID?: string
  setBounds: (newBounds: Point[]) => void
}

const OrderPoints: React.FC<OrderPointsProps> = ({orderID, setBounds}) => {
  const {data} = useOrdersState()

  if (!orderID) {
    return null
  }

  const activeOrder: OrderType | undefined = orderID
    ? data?.orders.filter(isTariff).find(o => o.orderid === orderID)
    : undefined

  if (activeOrder) {
    return getActiveOrderPoints(activeOrder, setBounds)
  }

  return <HistoryOrderPoints orderID={orderID} setBounds={setBounds} />
}

type HistoryOrdersProps = {
  orderID: string
  setBounds: (newBounds: Point[]) => void
}

const HistoryOrderPoints: React.FC<HistoryOrdersProps> = ({orderID, setBounds}) => {
  const {data} = useHistoryItemQuery(orderID)

  const route = useMemo(() => {
    const route = data?.order?.route

    if (!route) {
      return undefined
    }

    const destinations = route.destinations.map(destination => destination.point as Point)
    const source = route.source.point as Point

    return [source].concat(destinations)
  }, [data])

  return route ? <Points points={route} setBounds={setBounds} /> : null
}

const getActiveOrderPoints = (order: OrderType, setBounds: (newBounds: Point[]) => void) => {
  const route = order?.request.route
  const points: Point[] | void = route && route.length > 0 ? route.map(p => p.geopoint) : undefined
  const vehicle: Point | void = order?.vehicle?.location

  return <Points points={points} setBounds={setBounds} vehicle={vehicle} />
}

export default OrderPoints
